/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { IPublicClientApplication } from '@azure/msal-browser';
import { prepareMsalCache } from './authCache';
import { loginRequest, msalConfig } from './authConfig';

const isTestMode =
  import.meta.env.MODE === 'test' ||
  process.env.NODE_ENV === 'test' ||
  process.env.VITEST === 'true';
const shouldStartInteractiveLogin = !isTestMode;

const testPublicClientApplication = {
  acquireTokenRedirect: async () => undefined,
  acquireTokenSilent: async () => ({ idToken: '', idTokenClaims: {} }),
  getAllAccounts: () => [],
  handleRedirectPromise: async () => null,
} as unknown as IPublicClientApplication;

const createPublicClientApplication = async (): Promise<IPublicClientApplication> => {
  const msal = await import('@azure/msal-browser');
  prepareMsalCache(msalConfig.cache.cacheLocation, msalConfig.auth.clientId);
  const instance = await msal.createStandardPublicClientApplication(msalConfig);

  try {
    await instance.handleRedirectPromise();
  } catch (error) {
    // Redirect handling can fail on a stale/partial callback, but cached auth may still be usable.
    // Keep initialization alive so the app can recover from persisted accounts instead of showing
    // a fatal splash screen forever.
    // biome-ignore lint/suspicious/noConsole: auth bootstrap failure should be surfaced in logs
    console.error('Failed to complete MSAL redirect initialization', error);
  }
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    await instance.loginRedirect(loginRequest);
  }
  return instance;
};

export const pca = shouldStartInteractiveLogin
  ? createPublicClientApplication()
  : Promise.resolve(testPublicClientApplication);
